import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Award,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Clock3,
  ExternalLink,
  FileDown,
  LockKeyhole,
  Menu,
  PlayCircle,
  Send,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

function flattenActivities(course) {
  return (course?.modules || []).flatMap((module) =>
    (module.activities || []).map((activity) => ({
      ...activity,
      moduleTitle: module.title,
    }))
  );
}

function activityState(activity) {
  return activity?.progress?.state || {};
}

function optionLabel(option) {
  if (typeof option === "object" && option !== null) {
    return (
      option.label ??
      option.value ??
      option.id ??
      "Option"
    );
  }

  return String(option);
}

function optionValue(option, index) {
  if (typeof option === "object" && option !== null) {
    return String(
      option.value ??
        option.id ??
        option.label ??
        index
    );
  }

  return String(option);
}

function LearnerActivity({
  activity,
  state,
  setState,
}) {
  const content = activity.content || {};

  if (activity.activity_type === "content") {
    return (
      <div className="whitespace-pre-wrap text-base leading-8 text-slate-700">
        {content.body}
      </div>
    );
  }

  if (activity.activity_type === "video") {
    return (
      <div className="space-y-4">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-950">
          <video
            src={content.url}
            controls
            className="aspect-video w-full"
            preload="metadata"
          >
            Your browser does not support embedded video.
          </video>
        </div>

        <a
          href={content.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm font-semibold text-safety"
        >
          Open video in a new tab
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    );
  }

  if (activity.activity_type === "download") {
    return (
      <a
        href={content.url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 font-semibold text-navy hover:border-safety"
      >
        <FileDown className="h-5 w-5 text-safety" />
        Download learning resource
      </a>
    );
  }

  if (activity.activity_type === "external_link") {
    return (
      <a
        href={content.url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 font-semibold text-navy hover:border-safety"
      >
        <ExternalLink className="h-5 w-5 text-safety" />
        Open external learning resource
      </a>
    );
  }

  if (activity.activity_type === "reflection") {
    return (
      <div>
        <p className="whitespace-pre-wrap text-slate-700">
          {content.prompt ||
            "Record your reflection below."}
        </p>

        <textarea
          value={state.response || ""}
          onChange={(event) =>
            setState({
              ...state,
              response: event.target.value,
            })
          }
          rows={8}
          className="mt-5 w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 outline-none focus:border-safety focus:ring-2 focus:ring-safety/20"
          placeholder="Type your reflection here…"
        />
      </div>
    );
  }

  if (
    activity.activity_type ===
    "acknowledgement"
  ) {
    return (
      <label className="flex cursor-pointer gap-4 rounded-xl border border-slate-200 bg-slate-50 p-5">
        <input
          type="checkbox"
          checked={
            state.acknowledged === true
          }
          onChange={(event) =>
            setState({
              ...state,
              acknowledged:
                event.target.checked,
            })
          }
          className="mt-1 h-5 w-5 accent-orange-500"
        />

        <span className="leading-7 text-slate-700">
          {content.statement}
        </span>
      </label>
    );
  }

  if (activity.activity_type === "scenario") {
    const options =
      content.options || [];

    const answer =
      state.answers?.["0"] ?? "";

    return (
      <div>
        <div className="whitespace-pre-wrap rounded-2xl border border-slate-200 bg-slate-50 p-5 leading-7 text-slate-700">
          {content.scenario}
        </div>

        <div className="mt-6 space-y-3">
          {options.map(
            (option, index) => {
              const value =
                optionValue(
                  option,
                  index
                );

              return (
                <label
                  key={`${value}-${index}`}
                  className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4 hover:border-safety"
                >
                  <input
                    type="radio"
                    name={`scenario-${activity.id}`}
                    value={value}
                    checked={
                      answer === value
                    }
                    onChange={(event) =>
                      setState({
                        ...state,

                        answers: {
                          ...(state.answers ||
                            {}),

                          "0":
                            event.target
                              .value,
                        },
                      })
                    }
                    className="mt-1"
                  />

                  <span className="text-slate-700">
                    {optionLabel(
                      option
                    )}
                  </span>
                </label>
              );
            }
          )}
        </div>
      </div>
    );
  }

  if (
    [
      "mcq",
      "multi_select",
      "true_false",
      "assessment",
    ].includes(activity.activity_type)
  ) {
    const questions =
      content.questions || [];

    return (
      <div className="space-y-8">
        {questions.map(
          (
            question,
            questionIndex
          ) => {
            const key = String(
              question.id ??
                questionIndex
            );

            const options =
              question.options ||
              (activity.activity_type ===
              "true_false"
                ? ["True", "False"]
                : []);

            const current =
              state.answers?.[key];

            const selected =
              Array.isArray(current)
                ? current
                : current == null
                ? []
                : [current];

            const multi =
              activity.activity_type ===
                "multi_select" ||
              question.multiple === true ||
              question.question_type ===
                "multiple_response";

            return (
              <fieldset
                key={key}
                className="rounded-2xl border border-slate-200 p-5 md:p-6"
              >
                <legend className="px-2 font-semibold text-navy">
                  {question.prompt ||
                    `Question ${
                      questionIndex +
                      1
                    }`}
                </legend>

                <div className="mt-3 space-y-3">
                  {options.map(
                    (
                      option,
                      optionIndex
                    ) => {
                      const value =
                        optionValue(
                          option,
                          optionIndex
                        );

                      const checked =
                        selected.includes(
                          value
                        );

                      return (
                        <label
                          key={`${value}-${optionIndex}`}
                          className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-4 hover:border-safety"
                        >
                          <input
                            type={
                              multi
                                ? "checkbox"
                                : "radio"
                            }
                            name={`question-${activity.id}-${key}`}
                            value={
                              value
                            }
                            checked={
                              checked
                            }
                            onChange={(
                              event
                            ) => {
                              const answers =
                                {
                                  ...(state.answers ||
                                    {}),
                                };

                              if (
                                multi
                              ) {
                                const next =
                                  new Set(
                                    selected
                                  );

                                if (
                                  event
                                    .target
                                    .checked
                                ) {
                                  next.add(
                                    value
                                  );
                                } else {
                                  next.delete(
                                    value
                                  );
                                }

                                answers[
                                  key
                                ] =
                                  Array.from(
                                    next
                                  );
                              } else {
                                answers[
                                  key
                                ] =
                                  value;
                              }

                              setState(
                                {
                                  ...state,
                                  answers,
                                }
                              );
                            }}
                            className="mt-1"
                          />

                          <span className="text-slate-700">
                            {optionLabel(
                              option
                            )}
                          </span>
                        </label>
                      );
                    }
                  )}
                </div>
              </fieldset>
            );
          }
        )}
      </div>
    );
  }

  return (
    <p className="text-slate-600">
      This activity type is not yet available in the learner player.
    </p>
  );
}

export default function LMSCoursePlayer() {
  const { enrolmentId } =
    useParams();

  const navigate = useNavigate();

  const [course, setCourse] =
    useState(null);

  const [currentId, setCurrentId] =
    useState(null);

  const [state, setState] =
    useState({});

  const [loading, setLoading] =
    useState(true);

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const [notice, setNotice] =
    useState("");

  const [feedback, setFeedback] =
    useState([]);

  const [
    sidebarOpen,
    setSidebarOpen,
  ] = useState(false);

  const currentIdRef =
    useRef(null);

  const activities = useMemo(
    () => flattenActivities(course),
    [course]
  );

  const current =
    activities.find(
      (activity) =>
        activity.id === currentId
    ) || null;

  const currentIndex = current
    ? activities.findIndex(
        (activity) =>
          activity.id === current.id
      )
    : -1;

  const load = async (
    preferredId = null
  ) => {
    setError("");

    try {
      const me = await lmsApi.get(
        "/auth/me"
      );

      if (
        me.data
          .require_password_change
      ) {
        navigate(
          "/learn/change-password",
          {
            replace: true,
          }
        );

        return;
      }

      const response =
        await lmsApi.get(
          `/player/enrolments/${enrolmentId}`
        );

      const nextCourse =
        response.data;

      setCourse(nextCourse);

      const flat =
        flattenActivities(
          nextCourse
        );

      const preferred =
        preferredId ||
        currentIdRef.current ||
        nextCourse.current_activity_id;

      const next =
        flat.find(
          (item) =>
            item.id === preferred
        ) ||
        flat.find(
          (item) => item.accessible
        ) ||
        flat[0] ||
        null;

      setCurrentId(
        next?.id || null
      );

      currentIdRef.current =
        next?.id || null;

      setState(
        activityState(next)
      );
    } catch (err) {
      if (
        err.response?.status === 401
      ) {
        navigate("/learn/login", {
          replace: true,
        });

        return;
      }

      setError(
        err.response?.data?.detail ||
          "We could not load this course."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolmentId]);

  useEffect(() => {
    if (!currentId) {
      return undefined;
    }

    currentIdRef.current =
      currentId;

    const selected =
      activities.find(
        (activity) =>
          activity.id === currentId
      );

    setState(
      activityState(selected)
    );

    setNotice("");
    setFeedback([]);
    setError("");

    if (
      selected?.accessible &&
      !["completed", "failed"].includes(
        selected.progress.status
      )
    ) {
      lmsApi
        .post(
          `/player/enrolments/${enrolmentId}/activities/${currentId}/start`
        )
        .catch(() => {});
    }

    const timer =
      window.setInterval(() => {
        if (
          document.visibilityState ===
            "visible" &&
          selected?.accessible &&
          selected.progress.status !==
            "failed"
        ) {
          lmsApi
            .post(
              `/player/enrolments/${enrolmentId}/activities/${currentId}/heartbeat`
            )
            .catch(() => {});
        }
      }, 30000);

    return () =>
      window.clearInterval(timer);
  }, [
    currentId,
    enrolmentId,
    activities,
  ]);

  const saveState = async () => {
    if (!current) {
      return;
    }

    setBusy(true);
    setError("");

    try {
      await lmsApi.put(
        `/player/enrolments/${enrolmentId}/activities/${current.id}/state`,
        {
          state,
        }
      );

      setNotice(
        "Progress saved."
      );

      await load(current.id);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Your progress could not be saved."
      );
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!current) {
      return;
    }

    setBusy(true);
    setError("");
    setNotice("");
    setFeedback([]);

    try {
      const response =
        await lmsApi.post(
          `/player/enrolments/${enrolmentId}/activities/${current.id}/complete`,
          {
            state,
          }
        );

      const result =
        response.data;

      setFeedback(
        result.feedback || []
      );

      if (result.message) {
        setNotice(
          result.message
        );
      } else if (
        result.passed === true &&
        result.score_percent != null
      ) {
        setNotice(
          `Passed with ${result.score_percent}%.`
        );
      } else {
        setNotice(
          "Activity completed."
        );
      }

      const oldIndex =
        currentIndex;

      await load(current.id);

      if (
        result.course_completed
      ) {
        return;
      }

      if (
        result.activity_status !==
        "completed"
      ) {
        return;
      }

      window.setTimeout(() => {
        setCourse(
          (latestCourse) => {
            const nextActivities =
              flattenActivities(
                latestCourse
              );

            const next =
              nextActivities
                .slice(
                  oldIndex + 1
                )
                .find(
                  (item) =>
                    item.accessible &&
                    item.progress
                      .status !==
                      "completed"
                );

            if (next) {
              setCurrentId(
                next.id
              );

              currentIdRef.current =
                next.id;
            }

            return latestCourse;
          }
        );
      }, 0);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "This activity could not be completed."
      );
    } finally {
      setBusy(false);
    }
  };

  const selectActivity = (
    activity
  ) => {
    if (!activity.accessible) {
      return;
    }

    setCurrentId(
      activity.id
    );

    currentIdRef.current =
      activity.id;

    setSidebarOpen(false);
  };

  const move = (direction) => {
    let index =
      currentIndex + direction;

    while (
      index >= 0 &&
      index < activities.length
    ) {
      if (
        activities[index]
          .accessible
      ) {
        selectActivity(
          activities[index]
        );

        return;
      }

      index += direction;
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-slate-50 py-20">
        <div className="container-rail text-center text-slate-600">
          Loading your course…
        </div>
      </section>
    );
  }

  if (!course || !current) {
    return (
      <section className="min-h-screen bg-slate-50 py-20">
        <div className="container-rail">
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
            {error ||
              "This course does not contain any learning activities."}
          </div>
        </div>
      </section>
    );
  }

  const questionLike = [
    "mcq",
    "multi_select",
    "true_false",
    "scenario",
    "assessment",
  ].includes(
    current.activity_type
  );

  const failed =
    current.progress.status ===
    "failed";

  const actionLabel =
    current.progress.status ===
    "completed"
      ? "Completed"
      : failed
      ? "No attempts remaining"
      : questionLike
      ? "Submit activity"
      : "Mark complete";

  return (
    <>
      <SEO
        title={`${course.course_title} | TRAC-Learn`}
        description="Secure TRAC-Learn course player."
        path={`/learn/course/${enrolmentId}`}
      />

      <div className="min-h-screen bg-slate-50">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="flex min-h-16 items-center justify-between gap-4 px-4 md:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={() =>
                  navigate("/learn")
                }
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Back to learning dashboard"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>

              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-navy">
                  {
                    course.course_title
                  }
                </p>

                <p className="text-xs text-slate-500">
                  Version{" "}
                  {
                    course.version_number
                  }{" "}
                  ·{" "}
                  {
                    course.progress_percent
                  }
                  % complete
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                setSidebarOpen(
                  (value) => !value
                )
              }
              className="rounded-lg border border-slate-200 p-2 text-navy lg:hidden"
              aria-label="Course contents"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          <div className="h-1 bg-slate-100">
            <div
              className="h-full bg-safety transition-all"
              style={{
                width: `${Math.max(
                  0,
                  Math.min(
                    100,
                    course.progress_percent
                  )
                )}%`,
              }}
            />
          </div>
        </header>

        <div className="mx-auto flex max-w-[1600px]">
          <aside
            className={`${
              sidebarOpen
                ? "block"
                : "hidden"
            } fixed inset-x-0 bottom-0 top-[4.25rem] z-20 overflow-y-auto border-r border-slate-200 bg-white lg:sticky lg:top-[4.25rem] lg:block lg:h-[calc(100vh-4.25rem)] lg:w-80 lg:shrink-0`}
          >
            <div className="p-5">
              <p className="label-eyebrow text-safety">
                Course contents
              </p>

              <div className="mt-5 space-y-6">
                {course.modules.map(
                  (module) => (
                    <div
                      key={module.id}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h2 className="text-sm font-bold text-navy">
                          {module.title}
                        </h2>

                        {module.completed && (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        )}
                      </div>

                      <div className="mt-2 space-y-1">
                        {module.activities.map(
                          (
                            activity
                          ) => {
                            const selected =
                              activity.id ===
                              current.id;

                            return (
                              <button
                                type="button"
                                key={
                                  activity.id
                                }
                                onClick={() =>
                                  selectActivity(
                                    activity
                                  )
                                }
                                disabled={
                                  !activity.accessible
                                }
                                title={
                                  activity.locked_reason ||
                                  undefined
                                }
                                className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition ${
                                  selected
                                    ? "bg-navy text-white"
                                    : activity.accessible
                                    ? "text-slate-700 hover:bg-slate-100"
                                    : "cursor-not-allowed text-slate-400"
                                }`}
                              >
                                {activity
                                  .progress
                                  .status ===
                                "completed" ? (
                                  <CheckCircle2
                                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                                      selected
                                        ? "text-safety"
                                        : "text-emerald-600"
                                    }`}
                                  />
                                ) : activity
                                    .progress
                                    .status ===
                                  "failed" ? (
                                  <AlertTriangle
                                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                                      selected
                                        ? "text-red-300"
                                        : "text-red-500"
                                    }`}
                                  />
                                ) : activity.accessible ? (
                                  <Circle className="mt-0.5 h-4 w-4 shrink-0" />
                                ) : (
                                  <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" />
                                )}

                                <span>
                                  {
                                    activity.title
                                  }
                                </span>
                              </button>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </aside>

          <main className="min-w-0 flex-1 px-4 py-8 md:px-8 md:py-10 lg:px-12">
            <div className="mx-auto max-w-4xl">
              <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <span>
                  {
                    current.moduleTitle
                  }
                </span>

                <span>•</span>

                <span>
                  {current.activity_type.replaceAll(
                    "_",
                    " "
                  )}
                </span>

                {current.required && (
                  <span className="rounded-full bg-orange-50 px-2 py-1 text-safety">
                    Required
                  </span>
                )}

                {questionLike &&
                  current.attempts_remaining !=
                    null && (
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">
                      Attempts used{" "}
                      {
                        current.attempts_used
                      }{" "}
                      · remaining{" "}
                      {
                        current.attempts_remaining
                      }
                    </span>
                  )}
              </div>

              <h1 className="mt-3 font-display text-3xl font-bold text-navy md:text-5xl">
                {current.title}
              </h1>

              {failed && (
                <div className="mt-6 flex gap-3 rounded-xl border border-red-200 bg-red-50 p-5 text-red-800">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />

                  <div>
                    <p className="font-bold">
                      Assessment attempts exhausted
                    </p>

                    <p className="mt-1 text-sm">
                      This required assessment has not been passed. Your
                      training administrator will need to decide whether
                      another attempt should be authorised.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-9">
                <LearnerActivity
                  activity={current}
                  state={state}
                  setState={setState}
                />
              </div>

              {error && (
                <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                  {error}
                </div>
              )}

              {notice && (
                <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                  {notice}
                </div>
              )}

              {feedback.length >
                0 && (
                <div className="mt-6 space-y-3">
                  {feedback.map(
                    (item) => (
                      <div
                        key={
                          item.question_id
                        }
                        className={`rounded-xl border p-4 text-sm ${
                          item.correct
                            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                            : "border-amber-200 bg-amber-50 text-amber-900"
                        }`}
                      >
                        <p className="font-bold">
                          {item.correct
                            ? "Correct"
                            : "Review this response"}
                        </p>

                        {item.message && (
                          <p className="mt-1">
                            {
                              item.message
                            }
                          </p>
                        )}

                        {item.explanation && (
                          <p className="mt-2 text-slate-700">
                            {
                              item.explanation
                            }
                          </p>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}

              <div className="mt-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      move(-1)
                    }
                    disabled={
                      currentIndex <= 0
                    }
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      move(1)
                    }
                    disabled={
                      currentIndex >=
                      activities.length -
                        1
                    }
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-40"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex flex-wrap gap-3">
                  {[
                    "reflection",
                    "mcq",
                    "multi_select",
                    "true_false",
                    "scenario",
                    "assessment",
                  ].includes(
                    current.activity_type
                  ) &&
                    ![
                      "completed",
                      "failed",
                    ].includes(
                      current.progress
                        .status
                    ) && (
                      <button
                        type="button"
                        onClick={
                          saveState
                        }
                        disabled={busy}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-50"
                      >
                        <Clock3 className="h-4 w-4" />
                        Save progress
                      </button>
                    )}

                  <button
                    type="button"
                    onClick={complete}
                    disabled={
                      busy ||
                      [
                        "completed",
                        "failed",
                      ].includes(
                        current.progress
                          .status
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-navy px-5 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {questionLike ? (
                      <Send className="h-4 w-4" />
                    ) : (
                      <PlayCircle className="h-4 w-4" />
                    )}

                    {busy
                      ? "Saving…"
                      : actionLabel}
                  </button>
                </div>
              </div>

              {course.status ===
                "completed" && (
                <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-900">
                  <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-6 w-6" />

                      <div>
                        <h2 className="font-display text-xl font-bold">
                          Course complete
                        </h2>

                        <p className="mt-1 text-sm">
                          Your completion has been recorded in TRAC-Learn and
                          a certificate is available.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/learn/certificate/${enrolmentId}`
                        )
                      }
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-3 text-sm font-bold text-white"
                    >
                      <Award className="h-4 w-4" />
                      View certificate
                    </button>
                  </div>
                </div>
              )}

              {course.status ===
                "failed" && (
                <div className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-6 text-red-900">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-6 w-6 shrink-0" />

                    <div>
                      <h2 className="font-display text-xl font-bold">
                        Course completion blocked
                      </h2>

                      <p className="mt-1 text-sm">
                        A required assessment has exhausted its permitted
                        attempts. This remains recorded for your training
                        administrator to review.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}